/**
 * Lo que dice el panel sobre el certificado oficial. Sin React y sin Word, para poder probarlo.
 *
 * El oficial se paga en Stripe (59 € por título, IVA incluido), ya no con una clave de Payhip. La cifra la da el
 * servidor: si no contesta, el panel no enseña ninguna y no inventa.
 */
export type PrecioDelCertificado = { disponible: boolean; importe_cent: number | null; moneda?: string; iva_incluido?: boolean; admite_codigo?: boolean; motivo?: "motor_viejo" } | null;

/** «59 €», «59,50 €». Sin cifra del servidor, nada. */
export function eurosDelCertificado(p: PrecioDelCertificado): string | null {
  const c = p?.importe_cent;
  if (typeof c !== "number" || !Number.isFinite(c) || c <= 0) return null;
  return `${(c / 100).toFixed(2).replace(".", ",").replace(/,00$/, "")} €`;
}

/** La frase de arriba del apartado: qué es, qué cuesta el oficial y qué viaja. */
export function fraseDelCertificado(p: PrecioDelCertificado): string {
  const e = eurosDelCertificado(p);
  return "Registra CÓMO se hizo el libro. El borrador es gratis; el oficial, con un código QR que cualquiera puede comprobar, "
    + (e ? `cuesta ${e} por título, IVA incluido.` : "se paga aparte, por título.")
    + " Tu texto no sale del ordenador: solo viajan sus huellas, el título y el autor.";
}

/** ¿Se puede pagar desde aquí? Hace falta que el servidor diga que sí y a cuánto. */
export const sePuedePagar = (p: PrecioDelCertificado): boolean => !!p && p.disponible === true && eurosDelCertificado(p) !== null;

/**
 * EL CÓDIGO DE DESCUENTO DEL CERTIFICADO. La casilla solo se enseña si el servidor dice que hay códigos para el
 * certificado (el de la comunidad no vale aquí): un panel que la enseñara siempre invitaría a probarlo.
 */
export const admiteCodigo = (p: PrecioDelCertificado): boolean => sePuedePagar(p) && p?.admite_codigo === true;
/** Lo que se dice al abrir la página de pago con un código: lo que queda por pagar. A cero euros, que hay que completarla igual. */
export function fraseConCodigo(importe_cent: number | null | undefined): string {
  if (typeof importe_cent !== "number" || !Number.isFinite(importe_cent) || importe_cent < 0) return "Tu código está puesto.";
  if (importe_cent === 0) return "Con tu código, este certificado no te cuesta nada. Complétalo igualmente en la página de pago: no te pide tarjeta.";
  return `Con tu código, este certificado te cuesta ${(importe_cent / 100).toFixed(2).replace(".", ",").replace(/,00$/, "")} €.`;
}

/**
 * QUÉ TOCA EN LA PESTAÑA DEL CERTIFICADO (Rais, 12-oct-2026). Con el borrador hecho se enciende el amarillo, «Hacerlo
 * oficial»: mientras ese es el paso que toca, el azul de «Generar certificado» se APAGA —más oscuro, como inactivo—,
 * y nada más cambia. (La primera vez se entendió «un solo botón relleno en todo el panel» y se cambiaron el menú y los
 * demás botones; no era eso, y se deshizo: solo este botón.)
 *   "generar"  no hay borrador: toca generar
 *   "oficial"  hay borrador sin registrar: toca «Hacerlo oficial», y «Generar» se apaga
 *   "hecho"    ya es oficial: nada que pagar
 */
export type PasoDelCertificado = "generar" | "oficial" | "hecho";
export const pasoDelCertificado = (hayBorrador: boolean, oficial: boolean): PasoDelCertificado => (!hayBorrador ? "generar" : oficial ? "hecho" : "oficial");
/** Cuánto se ve «Generar certificado»: apagado mientras toca el amarillo; a medias si no se puede pulsar; entero si no. */
export const OPACIDAD_APAGADO = 0.38;
export const opacidadDeGenerar = (paso: PasoDelCertificado, sinPoder: boolean): number => (paso === "oficial" ? OPACIDAD_APAGADO : sinPoder ? 0.6 : 1);

export const rotuloDeHacerOficial = (p: PrecioDelCertificado, ocupado: boolean): string =>
  ocupado ? "Abriendo el pago…" : `▸ Hacerlo oficial · ${eurosDelCertificado(p) ?? ""}`.replace(/ · $/, "");

export const FRASE_PAGO_ABIERTO = "Se ha abierto la página de pago en tu navegador. Paga allí con tarjeta y vuelve aquí: lo veré solo.";
export const FRASE_PAGO_SIN_ABRIR = "Abre la página de pago con el enlace de abajo. Paga allí con tarjeta y vuelve aquí: lo veré solo.";
export const FRASE_AUN_NO = "Todavía no veo el pago. Si acabas de pagar, espera unos segundos y pulsa otra vez. Si no has pagado, no se ha cobrado nada.";
export const FRASE_SIN_PAGO_DISPONIBLE = "El pago del certificado oficial no está disponible ahora mismo. Tu borrador sigue en su sitio; prueba más tarde.";
/** El programa instalado es anterior a esto: «prueba más tarde» sería mentira, lo que toca es actualizar. */
export const FRASE_MOTOR_VIEJO = "Para hacerlo oficial desde aquí hace falta actualizar HumanInk en este ordenador. Tu borrador sigue en su sitio.";
export const fraseSinPago = (p: PrecioDelCertificado): string => (p?.motivo === "motor_viejo" ? FRASE_MOTOR_VIEJO : FRASE_SIN_PAGO_DISPONIBLE);
/** Pasado el tope se deja de preguntar solo: se dice, porque antes se prometió «lo veré solo». */
export const FRASE_DEJO_DE_MIRAR = "He dejado de mirar si llegaba el pago. Si ya has pagado, pulsa «Ya he pagado»; si no, la página de pago sigue abierta un rato más.";

/** Lo que se dice cuando ya es oficial: que el QR verifica y dónde está el PDF. */
export function fraseDeOficial(r: { pdf?: string | null; yaEstaba?: boolean }): string {
  return (r.yaEstaba ? "Este certificado ya estaba registrado. " : "Certificado oficial registrado: el código QR ya lo verifica en público. ")
    + (r.pdf ? `El PDF oficial está en ${r.pdf}.` : "No he podido guardar el PDF oficial; el registro sí está hecho.");
}

/**
 * UN LIBRO ESCRITO POR ESCENAS con alguna sin registro: el certificado no da cifra del libro entero (Rais, 12-oct-2026).
 * Se dice al generar el borrador y, sobre todo, ANTES de pagar el oficial: se paga un certificado que queda así.
 */
export type EscenasDelCertificado = { con?: number; de?: number; cambiadas?: number; por_leer?: number; fuera?: number; sinContar?: boolean };
export function escenasSinRegistro(autoria: { escenas?: EscenasDelCertificado | null } | null | undefined): { con: number; de: number } | null {
  const e = autoria?.escenas;
  return e && Number.isInteger(e.con) && Number.isInteger(e.de) && e.de! > 0 && e.con! < e.de! ? { con: e.con!, de: e.de! } : null;
}
export function fraseDeLasEscenas(autoria: { escenas?: EscenasDelCertificado | null } | null | undefined): string | null {
  // Las escenas de este documento no se han podido contar (no es el manuscrito ni la novela montada del libro), o tiene
  // texto fuera de ellas: tampoco hay cifra.
  if (autoria?.escenas?.sinContar === true) return "Este certificado no lleva cifra del libro: en esta carpeta se escribe un libro por escenas y no he podido contar las de este documento. Genera el certificado desde el manuscrito o desde la novela montada. Lo dice así en el PDF.";
  const fuera = autoria?.escenas?.fuera;
  const e = escenasSinRegistro(autoria);
  if (!e && typeof fuera === "number" && fuera > 0) return `Este certificado no lleva cifra del libro: ${fuera} palabras del documento están fuera de sus escenas cerradas (antes de la primera, entre capítulos, o pegadas sin guardar), y de ellas no hay registro. Si son de una escena, guarda, verifícala y ciérrala; fuera de las escenas se admite hasta un 3 % del libro. Lo dice así en el PDF.`;
  const k = cajonesDeLasEscenas(autoria);
  if (!e || !k) return null;
  const { sin, c, pl, resto } = k;
  const cambiaron = c === 0 ? "" : ` ${c === 1 ? "Una escena cambió" : `${c} escenas cambiaron`} después de cerrarse: ${c === 1 ? "ciérrala" : "ciérralas"} otra vez (y verifícala antes si le has añadido texto).`;
  // Lo que no puede llevar cifra es el LIBRO: esa escena, en el panel, sí enseña «al menos X %». Y no tiene arreglo. Sin
  // decir cuántas tandas: el motor cuenta escenas (revisión de la ronda 1 del encargo 0009).
  const porLeer = pl === 0 ? "" : ` En ${pl === 1 ? "una escena" : `${pl} escenas`} hay cambios de una versión de HumanInk anterior al 10 de octubre. No se pueden medir ni arreglar, y con ellos el libro no puede llevar cifra.`;
  const sinNada = resto === 0 ? "" : `, y de ${resto === 1 ? (sin === 1 ? "la otra" : "otra") : (c === 0 && pl === 0 ? `las otras ${resto}` : `otras ${resto}`)} no consta cómo se ${resto === 1 ? "escribió" : "escribieron"}`;
  return `Este certificado no lleva cifra del libro: solo ${e.con} de ${e.de} escenas tienen registro${pl ? " completo" : ""} en HumanInk${sinNada}.${cambiaron}${porLeer} Lo dice así en el PDF.`;
}
/**
 * Por qué faltan escenas, en cajones que no se pisan:
 *   c       cambiaron después de cerrarse: SÍ tienen registro, pero de otro texto, y la salida es volver a cerrarlas
 *   pl      a medias (`por_leer`): tienen registro, pero no completo. Desde el encargo 0009 solo pasa con cambios de una
 *           versión de antes del 10-oct, que no guardaba la propuesta: no tiene arreglo, ni con el botón ni cerrando
 *   resto   no consta cómo se escribieron: la salida es verificarlas y cerrarlas
 */
function cajonesDeLasEscenas(autoria: { escenas?: EscenasDelCertificado | null } | null | undefined): { sin: number; c: number; pl: number; resto: number } | null {
  const e = escenasSinRegistro(autoria);
  if (!e) return null;
  const sin = e.de - e.con;
  const c = Math.max(0, Math.min(Number(autoria?.escenas?.cambiadas) || 0, sin));
  const pl = Math.max(0, Math.min(Number(autoria?.escenas?.por_leer) || 0, sin - c));
  return { sin, c, pl, resto: sin - c - pl };
}
/** Lo de antes de pagar un oficial que queda sin cifra, cuando la cifra llega cerrando escenas. */
export const FRASE_OFICIAL_SIN_CIFRA = "Si lo haces oficial ahora, se registra así, sin cifra. Para que la lleve, verifica y cierra las escenas que faltan y genera otro borrador.";
/**
 * LO DE ANTES DE PAGAR, SEGÚN LA CAUSA (revisión de la ronda 1 del encargo 0009). Va detrás de `fraseDeLasEscenas`, que
 * dice la causa. Si la cifra llega cerrando escenas (las que faltan, las que cambiaron, el texto fuera de ellas), se dice
 * eso. Si hay escenas a medias, no llega nunca: mandar a cerrar las demás era un callejón justo antes de pagar. Si pasan
 * las dos cosas, que se pueden cerrar y que ni así la llevará.
 */
export function fraseOficialSinCifra(autoria: { escenas?: EscenasDelCertificado | null } | null | undefined): string {
  const k = cajonesDeLasEscenas(autoria);
  if (!k || k.pl === 0) return FRASE_OFICIAL_SIN_CIFRA;
  const tambien = k.c + k.resto > 0 || (Number(autoria?.escenas?.fuera) || 0) > 0;
  return `Si lo haces oficial ahora, se registra así, sin cifra. Y no la tendrá aunque ${tambien ? "cierres las escenas que faltan" : "generes otro borrador"}: los cambios de ${k.pl === 1 ? "esa escena" : `esas ${k.pl} escenas`} no se pueden medir.`;
}

/** ¿Lleva cifra el borrador que ha contestado el motor? Solo con un número: sin él (o de un motor que no la manda), no. */
export const llevaCifra = (borrador: { has?: unknown } | null | undefined): boolean => typeof borrador?.has === "number" && Number.isFinite(borrador.has);

/**
 * LAS TANDAS DE CAMBIOS QUE NO CONSTAN (encargo 0005): las que se llevaron a Word desde la pestaña Escritura y no se
 * han podido leer. Lo que el autor DECIDIERA en ellas no consta. Se dice cuáles y cómo arreglarlo. El borrador es
 * gratis: repetirlo no cuesta.
 *
 * Cada frase habla solo de las suyas, y solo se manda al botón «La decidí en otro documento» con las que el botón lee:
 *   sin_decidir, con_marcas   su Word sigue como se entregó, o con cambios sin decidir: el botón de la pestaña Escritura
 *   sin_fichero               su Word ya no está en la carpeta: el panel no enseña el botón (ni lo leería); que le
 *                             devuelva su nombre
 *   las demás                 (sin_escena, error) no se pudieron mirar: solo se dice que no constan
 *
 * `conCifra`: si el borrador lleva cifra. Con ella, las palabras de la IA de esas tandas que estén en el texto sí cuentan
 * (encargo 0009: la medida mira lo que hay en el texto cerrado), y se dice al final, detrás de lo que hay que hacer. Sin
 * cifra no cuenta nada, y no se dice (equipo rojo, revisión de la ronda 1: el PDF decía «no se da una cifra» y, debajo,
 * «cuenta»). Sin saberlo, tampoco.
 */
export type TandaSinLeer = { escena: string; version: string; estado: string };
const CON_BOTON = ["sin_decidir", "con_marcas"];
export function fraseDeTandasSinLeer(lista: TandaSinLeer[] | null | undefined, conCifra = false): string {
  const t = Array.isArray(lista) ? lista : [];
  const conBoton = t.filter((x) => CON_BOTON.includes(x.estado));
  const idas = t.filter((x) => x.estado === "sin_fichero");
  const otras = t.filter((x) => !CON_BOTON.includes(x.estado) && x.estado !== "sin_fichero");
  const grupos = [conBoton, idas, otras].filter((g) => g.length).length;
  const frases: string[] = [];
  const cuales = (g: TandaSinLeer[]) => g.slice(0, 3).map((x) => `escena ${x.escena}, en «${x.version}»`).join("; ") + (g.length > 3 ? `; y ${g.length - 3} más` : "");
  // La primera abre con «Ojo»; las que vienen detrás, «Tampoco consta…».
  const noConstan = (g: TandaSinLeer[], dichas = cuales(g)) => {
    const una = g.length === 1;
    return frases.length === 0
      ? `Ojo: lo que decidiste en ${una ? "una tanda" : `${g.length} tandas`} de cambios (${dichas}) no consta`
      : `Tampoco consta lo que decidiste en ${una ? "otra tanda" : `otras ${g.length} tandas`} de cambios (${dichas})`;
  };
  if (conBoton.length) {
    const una = conBoton.length === 1;
    frases.push(`${noConstan(conBoton)}. Si ${una ? "la" : "las"} decidiste en otro documento, ábrelo, pulsa «La decidí en otro documento» en la pestaña Escritura y vuelve a generar el borrador.`);
  }
  if (idas.length) {
    frases.push(idas.length === 1
      ? `${noConstan(idas, `escena ${idas[0].escena}`)} porque «${idas[0].version}» ya no está en la carpeta. Devuélvele su nombre y genera otro borrador.`
      : `${noConstan(idas)} porque sus documentos ya no están en la carpeta. Devuélveles su nombre y genera otro borrador.`);
  }
  if (otras.length) frases.push(`${noConstan(otras)}.`);
  if (conCifra && t.length) frases.push(`Las palabras de la IA de ${grupos > 1 ? "todas esas tandas" : t.length === 1 ? "esa tanda" : "esas tandas"} que estén en el texto sí cuentan.`);
  return frases.join(" ");
}

/** Cada cuánto se pregunta por el pago mientras se espera, y hasta cuándo. Después, queda el botón. */
export const ESPERA_DEL_PAGO = Object.freeze({ cada_ms: 5000, tope_ms: 20 * 60 * 1000 });
